/**
 * Daraxt ustidagi UCH amal — `id`/`parentId` juftligi bo'lgan HAR QANDAY
 * ro'yxat uchun.
 *
 * <h2>⚠️ Nega umumiy, nega har modulda o'zi emas</h2>
 * Bu uch funksiya ikki BOSHQA daraxtda kerak bo'ladi — tashkiliy tuzilma
 * (`platform.org_node`) va agent/reseller tarmog'i (`agent.party_node`) —
 * va ularning xatosi JIM: ekranda ogohlantirish chiqmaydi, tuzilma
 * shunchaki noto'g'ri ko'rinadi. Nusxa ajrashganda bitta daraxtda sikl
 * to'siladi, ikkinchisida esa yo'q — va buni faqat bazadagi natijaga
 * qarab sezish mumkin.
 */

export interface TreeNodeLike {
  id: string
  parentId: string | null
}

export type TreeOf<T> = T & { children: TreeOf<T>[] }

/**
 * Yassi ro'yxatdan daraxt.
 *
 * ⚠️ Otasi to'plamda BO'LMAGAN tugun ILDIZ sifatida qaytadi. Bu xato
 * emas, tor ko'rinish doirasining odatiy natijasi: reseller o'z shoxini
 * ko'radi, uning otasi esa doiradan tashqarida. Bunday tugunni tashlab
 * yuborish operatorga BO'SH daraxt ko'rsatardi.
 *
 * ⚠️ Chuqurlik cheklanmagan — bu funksiya qavatlarni SANAMAYDI. Tartib
 * kirish ro'yxatinikidan olinadi (ota doim bolasidan oldin kelsa daraxt
 * ham shu tartibda chiqadi).
 */
export function buildTree<T extends TreeNodeLike>(nodes: readonly T[]): TreeOf<T>[] {
  const byId = new Map<string, TreeOf<T>>()
  for (const node of nodes) {
    byId.set(node.id, { ...node, children: [] })
  }

  const roots: TreeOf<T>[] = []
  for (const node of nodes) {
    const built = byId.get(node.id)
    if (!built) continue
    const parent = node.parentId === null ? undefined : byId.get(node.parentId)
    if (parent) {
      parent.children.push(built)
    } else {
      roots.push(built)
    }
  }
  return roots
}

/**
 * Tugun va uning BARCHA avlodlarining id'lari.
 *
 * Ko'chirishda ikki joyda kerak: yangi ota sifatida ularni taqiqlash
 * (sikl) va "nechta tugun ta'sirlanadi" ni sanash.
 *
 * ⚠️ `path` prefiksi bilan emas, `parentId` zanjiri bilan hisoblanadi va
 * bu ataylab: `path` yorliqlari UUID'dan yasaladi va prefiks solishtiruvi
 * ("n1234…" "n1234…" ning prefiksi) qisman mos kelgan yorliqda YOLG'ON
 * rost berardi.
 */
export function collectSubtreeIds<T extends TreeNodeLike>(
  nodes: readonly T[],
  rootId: string,
): ReadonlySet<string> {
  const childrenOf = new Map<string, string[]>()
  for (const node of nodes) {
    if (node.parentId === null) continue
    const list = childrenOf.get(node.parentId)
    if (list) list.push(node.id)
    else childrenOf.set(node.parentId, [node.id])
  }

  const result = new Set<string>([rootId])
  const queue = [rootId]
  while (queue.length > 0) {
    const current = queue.pop()
    if (current === undefined) break
    for (const child of childrenOf.get(current) ?? []) {
      if (result.has(child)) continue
      result.add(child)
      queue.push(child)
    }
  }
  return result
}

/**
 * Tugundan ildizgacha bo'lgan zanjir — ildizdan boshlab.
 *
 * "Qayerdan qayerga ko'chirilyapti" ni odam o'qiydigan qilib ko'rsatish
 * uchun: `path` ning o'zi UUID yorliqlaridan iborat va ekranda hech narsa
 * anglatmaydi.
 */
export function ancestorChain<T extends TreeNodeLike>(
  nodes: readonly T[],
  nodeId: string | null,
): readonly T[] {
  if (nodeId === null) return []
  const byId = new Map(nodes.map((node) => [node.id, node]))
  const chain: T[] = []
  let current = byId.get(nodeId)
  // Halqa bazada taqiqlangan, lekin buzilgan ma'lumotda ekran OSILIB
  // qolmasligi kerak — shuning uchun ko'rilgan tugunlar belgilanadi.
  const seen = new Set<string>()
  while (current && !seen.has(current.id)) {
    seen.add(current.id)
    chain.unshift(current)
    current = current.parentId === null ? undefined : byId.get(current.parentId)
  }
  return chain
}
